import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Clock, CheckCircle2, Send, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = [
  "Incorrect product information",
  "Missing product / barcode",
  "App bug or technical issue",
  "Account & billing",
  "Ingredient rating query",
  "Partnership or press enquiry",
  "Other",
];

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", subject: topics[0], message: "", barcode: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
          barcode: form.barcode || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed (${res.status})`);
      }
      setStatus("success");
      toast.success("Message sent — we'll reply within one business day.");
    } catch (err) {
      setStatus("error");
      toast.error(err.message || "Could not send right now. Please try again.");
    }
  };

  return (
    <main className="bg-zoku-bg text-zoku-ink min-h-screen" data-testid="support-page">
      <Navbar />
      <section className="pt-28 pb-24 md:pt-36 md:pb-32 max-w-5xl mx-auto px-6 md:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-zoku-ink2 hover:text-zoku-orange transition-colors"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-10"
        >
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zoku-orange">Help & Support</p>
          <h1 className="mt-3 font-heading text-4xl md:text-6xl font-bold leading-[1.03] tracking-tight">
            We're here to <span className="font-editorial text-zoku-orange">help you.</span>
          </h1>
          <p className="mt-5 text-lg text-zoku-ink2 max-w-2xl">
            Have a question about a scan result, a product in our database, or your account? Reach
            out — we usually respond within one business day.
          </p>
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Info cards */}
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-zoku-orangeLight flex items-center justify-center">
                  <Mail className="w-5 h-5 text-zoku-orange" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zoku-ink3">
                    Email support
                  </div>
                  <a
                    href="mailto:support@zoku.in"
                    className="font-heading text-lg font-bold text-zoku-ink hover:text-zoku-orange transition-colors"
                  >
                    support@zoku.in
                  </a>
                </div>
              </div>
              <p className="mt-4 text-sm text-zoku-ink2">We aim to reply within 24 hours.</p>
            </div>

            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zoku-ink3">
                    Response time
                  </div>
                  <div className="font-heading text-lg font-bold text-zoku-ink">
                    Within 1 business day
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm text-zoku-ink2">Mon – Fri, 9 AM – 6 PM IST</p>
            </div>

            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="text-[11px] uppercase tracking-wider text-zoku-ink3 font-bold">
                Support hours
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                <li className="flex justify-between">
                  <span className="text-zoku-ink2">Monday – Friday</span>
                  <span className="font-semibold text-zoku-ink">9:00 AM – 6:00 PM IST</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-zoku-ink2">Saturday</span>
                  <span className="font-semibold text-zoku-ink">10:00 AM – 2:00 PM IST</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-zoku-ink2">Sunday</span>
                  <span className="font-semibold text-zoku-ink3">Closed</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7 bg-white rounded-3xl border border-stone-100 p-7 md:p-9 shadow-widget"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-zoku-ink">
              Send us a message
            </h2>
            <p className="mt-2 text-sm text-zoku-ink2">
              Fill in the details below. For urgent ingredient queries, include the product
              barcode.
            </p>

            {status === "success" ? (
              <div
                className="mt-8 bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex gap-4"
                data-testid="support-success"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-heading text-xl font-bold text-emerald-800">
                    Message sent!
                  </div>
                  <p className="text-sm text-emerald-700 mt-1">
                    Thanks for reaching out. We've received your message and will reply to{" "}
                    <strong>{form.email}</strong> within one business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setForm({ name: "", email: "", subject: topics[0], message: "", barcode: "" });
                      setStatus("idle");
                    }}
                    className="mt-4 text-sm font-semibold text-zoku-orange hover:text-zoku-orangeHover"
                    data-testid="send-another-btn"
                  >
                    Send another message →
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="support-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-zoku-ink3 mb-1.5">
                      Your name
                    </span>
                    <input
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Arjun Mehta"
                      className="w-full rounded-xl border border-stone-200 bg-zoku-bg px-4 py-3 text-zoku-ink placeholder:text-zoku-ink3 focus:outline-none focus:border-zoku-orange focus:ring-2 focus:ring-zoku-orangeLight transition"
                      data-testid="support-name-input"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-semibold uppercase tracking-wider text-zoku-ink3 mb-1.5">
                      Email address
                    </span>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      className="w-full rounded-xl border border-stone-200 bg-zoku-bg px-4 py-3 text-zoku-ink placeholder:text-zoku-ink3 focus:outline-none focus:border-zoku-orange focus:ring-2 focus:ring-zoku-orangeLight transition"
                      data-testid="support-email-input"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-zoku-ink3 mb-1.5">
                    Subject
                  </span>
                  <select
                    value={form.subject}
                    onChange={update("subject")}
                    className="w-full rounded-xl border border-stone-200 bg-zoku-bg px-4 py-3 text-zoku-ink focus:outline-none focus:border-zoku-orange focus:ring-2 focus:ring-zoku-orangeLight transition"
                    data-testid="support-subject-select"
                  >
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-zoku-ink3 mb-1.5">
                    Barcode (optional)
                  </span>
                  <input
                    value={form.barcode}
                    onChange={update("barcode")}
                    placeholder="e.g. 5 901234 123457"
                    className="w-full rounded-xl border border-stone-200 bg-zoku-bg px-4 py-3 text-zoku-ink placeholder:text-zoku-ink3 font-mono focus:outline-none focus:border-zoku-orange focus:ring-2 focus:ring-zoku-orangeLight transition"
                    data-testid="support-barcode-input"
                  />
                </label>

                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-zoku-ink3 mb-1.5">
                    Message
                  </span>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us what's going on…"
                    className="w-full rounded-xl border border-stone-200 bg-zoku-bg px-4 py-3 text-zoku-ink placeholder:text-zoku-ink3 focus:outline-none focus:border-zoku-orange focus:ring-2 focus:ring-zoku-orangeLight transition resize-none"
                    data-testid="support-message-input"
                  />
                </label>

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="inline-flex items-center gap-2 bg-zoku-ink text-white font-semibold rounded-full px-7 py-3.5 hover:bg-stone-800 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="support-submit-btn"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send message
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
